import { AppShell } from './components/AppShell.js';
import { Dashboard } from './pages/Dashboard.js';
import { DocumentLibrary } from './pages/DocumentLibrary.js';
import { DocumentDetail } from './pages/DocumentDetail.js';
import { FileTasksPage } from './pages/FileTasks.js';
import { TagsAdmin } from './pages/TagsAdmin.js';
import { StorageContainersAdmin } from './pages/StorageContainersAdmin.js';
import { StorageContainerDetail } from './pages/StorageContainerDetail.js';
import { TaggingRulesAdmin } from './pages/TaggingRulesAdmin.js';
import { FileWorkflowsAdmin } from './pages/FileWorkflowsAdmin.js';
import { CustomPropertiesAdmin } from './pages/CustomPropertiesAdmin.js';
import { Settings } from './pages/Settings.js';
import { ProjectsList } from './pages/ProjectsList.js';
import { ProjectDetail } from './pages/ProjectDetail.js';
import { WorkItemsList } from './pages/WorkItemsList.js';
import { WorkItemBoard } from './pages/WorkItemBoard.js';
import { WorkItemDetail } from './pages/WorkItemDetail.js';
import { WorkItemShortRedirect } from './pages/WorkItemShortRedirect.js';
import { ProjectShortRedirect } from './pages/ProjectShortRedirect.js';
import { Watching } from './pages/Watching.js';
import { StandaloneNotes } from './pages/StandaloneNotes.js';
import { DataSourcesRegistry } from './pages/DataSourcesRegistry.js';
import { TimelineLane } from './pages/TimelineLane.js';
import { ExternalClientsNest } from './pages/ExternalClientsNest.js';
import { Help } from './pages/Help.js';
import { HelpIndex } from './pages/HelpIndex.js';
import { NotFound } from './pages/NotFound.js';

export interface RouterProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

export function Router({ currentPath, onNavigate }: RouterProps) {
  const url = new URL(currentPath, 'http://localhost');
  const path = url.pathname.replace(/\/$/, '') || '/';
  const queryParams = url.searchParams;

  let content: JSX.Element;
  if (path === '/' || path === '/dashboard') {
    content = <Dashboard onNavigate={onNavigate} />;
  } else if (path === '/documents') {
    // ctsk-056 fix — pass `?nest=` down so sidebar-driven nest switches
    // (which only change the query string, not the pathname) actually
    // re-sync the page's nest filter. Without the prop, DocumentLibrary's
    // lazy initializer only reads the URL once on first mount.
    content = (
      <DocumentLibrary
        onNavigate={onNavigate}
        nestParam={queryParams.get('nest')}
        // CTSK-493 — /documents?q=<query> is the header search's
        // full-page escape hatch (design doc §3.8); same prop-piping
        // reason as ?nest=.
        qParam={queryParams.get('q')}
      />
    );
  } else if (path.startsWith('/documents/')) {
    const id = path.split('/')[2]!;
    content = <DocumentDetail id={id} onNavigate={onNavigate} />;
  } else if (path === '/notes' || path.startsWith('/notes/')) {
    // CTSK-514: /notes/<id> is a note's permalink — the wall, scrolled to that note.
    content = <StandaloneNotes focusId={path === '/notes' ? null : path.split('/')[2] ?? null} onNavigate={onNavigate} />;
  } else if (path === '/tasks/files') {
    content = <FileTasksPage />;
  } else if (path === '/tags') {
    content = <TagsAdmin onNavigate={onNavigate} />;
  } else if (path === '/tags/rules') {
    content = <TaggingRulesAdmin />;
  } else if (path === '/file-workflows') {
    content = <FileWorkflowsAdmin />;
  } else if (path === '/custom-properties') {
    content = <CustomPropertiesAdmin />;
  } else if (path === '/storage') {
    content = <StorageContainersAdmin onNavigate={onNavigate} />;
  } else if (path.startsWith('/storage/')) {
    const id = path.split('/')[2]!;
    content = <StorageContainerDetail id={id} onNavigate={onNavigate} />;
  } else if (path === '/settings' || path.startsWith('/settings/')) {
    // CTSK-346 — the sidebar nest-card Tools cell (AppShell, 0.1.8) has
    // deep-linked to /settings?section=tools&nestSlug=<slug> since day
    // one, but the router never passed the section through, so the
    // click always landed on Settings → General with dead params (the
    // "URL generated but resolver doesn't exist" bug under the
    // CTSK-344 EPIC). Resolve the section from the path segment
    // (/settings/tools) or the ?section= query; Settings itself
    // coerces unknown/disabled values back to General (CTSK-298), so
    // junk or stale deep-links still land on a functional tab.
    // CTSK-348 — &nestSlug= is passed through as a prop (same ctsk-056
    // lesson as /documents?nest=: pushState-only navigation doesn't
    // remount, so a lazy window.location read inside the panel would
    // go stale). Settings forwards it to ToolsPanel, which filters the
    // table to that nest and renders a clearable chip.
    const sectionSegment = path.startsWith('/settings/') ? path.split('/')[2] : undefined;
    content = (
      <Settings
        onNavigate={onNavigate}
        section={sectionSegment ?? queryParams.get('section') ?? undefined}
        nestSlug={queryParams.get('nestSlug')}
      />
    );
  } else if (path === '/projects') {
    content = <ProjectsList onNavigate={onNavigate} />;
  } else if (path.startsWith('/projects/')) {
    // Project detail (CTSK-146) — Sessions section lives here.
    const id = path.split('/')[2]!;
    content = <ProjectDetail id={id} onNavigate={onNavigate} />;
  } else if (path === '/workitems') {
    // Pass deep-link filter params from the Dashboard KPI clicks
    // (assignee=mine, stage=started, flag=overdue|blocking|attention).
    // Without these the cards used to land on an unfiltered list,
    // which dr.the0 flagged as a bug.
    content = (
      <WorkItemsList
        onNavigate={onNavigate}
        projectIdParam={queryParams.get('projectId') ?? undefined}
        assigneeParam={queryParams.get('assignee') ?? undefined}
        stageParam={queryParams.get('stage') ?? undefined}
        flagParam={queryParams.get('flag') ?? undefined}
      />
    );
  } else if (path === '/workitems/board') {
    content = <WorkItemBoard onNavigate={onNavigate} />;
  } else if (path.startsWith('/workitems/')) {
    const id = path.split('/')[2]!;
    content = <WorkItemDetail id={id} onNavigate={onNavigate} />;
  } else if (path.startsWith('/wi/')) {
    // CTSK-235 Phase 1 — short shareable URL. Resolves via
    // GET /api/v1/workitems/:idOrKey (which already accepts either
    // canonical key or externalId, since the CTSK-84 flip) and
    // canonicalizes the URL to /workitems/<ULID> before the user
    // sees the detail page. No server-side route needed.
    const workitemKey = decodeURIComponent(path.split('/')[2] ?? '');
    content = (
      <WorkItemShortRedirect
        workitemKey={workitemKey}
        queryString={url.search}
        onNavigate={onNavigate}
      />
    );
  } else if (path.startsWith('/pj/')) {
    // CTSK-235 Phase 2a — short shareable URL for projects. Resolves via
    // the projects list (small — no by-key API endpoint today) and
    // canonicalizes to /projects/<ULID>. See ProjectShortRedirect.tsx.
    const projectKey = decodeURIComponent(path.split('/')[2] ?? '');
    content = (
      <ProjectShortRedirect
        projectKey={projectKey}
        queryString={url.search}
        onNavigate={onNavigate}
      />
    );
  } else if (path.startsWith('/nt/')) {
    // CTSK-235 Phase 2a — short shareable URL for nests. Nests are
    // slug-addressed on the server; the web app's native-nest routes live
    // under /mission-control/nests/:slug. This branch just redirects.
    // Unknown slugs fall through to the target route's own NotFound.
    const slug = decodeURIComponent(path.split('/')[2] ?? '');
    const dest = `/mission-control/nests/${slug}${url.search}`;
    if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
      window.history.replaceState(null, '', dest);
    }
    // Use a microtask so the initial render commits before we navigate away.
    queueMicrotask(() => onNavigate(dest));
    content = (
      <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
        Opening nest <code>{slug}</code>…
      </div>
    );
  } else if (path.startsWith('/dc/')) {
    // CTSK-354 — short shareable URL for documents. CTSK-235 declared
    // /dc/:id in its route set but Phase 2a stopped at wi/pj/nt; the
    // copy-button sweep (CTSK-354) completes the set (operator-approved
    // in-ship). Documents are id-addressed — plain canonicalizing
    // redirect to /documents/:id, same replaceState pattern as /nt/.
    const id = decodeURIComponent(path.split('/')[2] ?? '');
    const destDoc = `/documents/${id}${url.search}`;
    if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
      window.history.replaceState(null, '', destDoc);
    }
    queueMicrotask(() => onNavigate(destDoc));
    content = (
      <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
        Opening document <code>{id}</code>…
      </div>
    );
  } else if (path.startsWith('/ct/')) {
    // CTSK-354 — short shareable URL for tools (completes the CTSK-235
    // set, see /dc/ above). Tools are slug-addressed; /tools/:slug is
    // the canonical tool URL (native pages today, NotFound for tools
    // without one — same contract as the CTSK-365 Source-for chip).
    const toolSlug = decodeURIComponent(path.split('/')[2] ?? '');
    const destTool = `/tools/${toolSlug}${url.search}`;
    if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
      window.history.replaceState(null, '', destTool);
    }
    queueMicrotask(() => onNavigate(destTool));
    content = (
      <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
        Opening tool <code>{toolSlug}</code>…
      </div>
    );
  } else if (path === '/watching') {
    // ctsk-073 — unified Watching panel (documents + work items).
    content = <Watching onNavigate={onNavigate} />;
  } else if (path === '/help') {
    // CTSK-356 — bare /help renders the help index: a catalog of all
    // HELP_PAGES slugs grouped by area (lib/help-catalog.ts). Wired
    // from the persistent app footer's "Help" link. (v1 rendered the
    // repo README here; the README remains available in the repo.)
    content = <HelpIndex onNavigate={onNavigate} />;
  } else if (path.startsWith('/help/')) {
    // v1.5 (0.1.51) — /help/<slug> renders a specific doc from the
    // docs/ tree. Slug → doc mapping lives in Help.tsx's HELP_PAGES.
    // Prior to this fix, PatsPanel's link to /help/pats-api hit the
    // catch-all NotFound because the router only matched /help exact.
    const slug = path.slice('/help/'.length);
    content = <Help slug={slug} onNavigate={onNavigate} />;
  } else if (path === '/tools/data-sources-registry') {
    // 0.1.8 — native UI for the data-sources-registry tool. Reads the
    // YAML from the tool's bound storage container; writes a new
    // document version on add-entry. See pages/DataSourcesRegistry.tsx.
    content = <DataSourcesRegistry onNavigate={onNavigate} />;
  } else if (path === '/tools/timeline-lane') {
    // Timeline Lane 1.0.0 — native UI for the timeline tool (separate
    // from DSR, same page shape). Four tabs over the registry/ YAML
    // files shared with the standalone D3 viz. See pages/TimelineLane.tsx.
    content = <TimelineLane onNavigate={onNavigate} />;
  } else if (path === '/mission-control/nests/external-clients') {
    // 0.1.53 — CTSK-166 Phase B skeleton — native UI for the External
    // Clients Nest. Lists client access-request documents in the
    // "External Clients" container, grouped by access/* state via a
    // tab strip. Approve action (Phase B2) lands in a follow-up.
    // See pages/ExternalClientsNest.tsx + NATIVE_NEST_TYPES.
    // CTSK-357 — pass `?tab=` down as a prop (same ctsk-056 lesson as
    // /documents?nest= and CTSK-348's &nestSlug=: pushState-only
    // navigation doesn't remount, so a lazy window.location read
    // inside the page would go stale). The nest tile links here with
    // ?tab=all; the page validates unknown values back to its default.
    content = (
      <ExternalClientsNest
        onNavigate={onNavigate}
        tabParam={queryParams.get('tab')}
      />
    );
  } else {
    content = <NotFound path={path} />;
  }

  return (
    <AppShell currentPath={path} onNavigate={onNavigate}>
      {content}
    </AppShell>
  );
}
